// Auth /me hardened caller — mobile-data resilience (Jul 3 2026).
//
// EXTRACTED from api.js so unit tests can import this module without
// pulling axios's ESM entry through jest's babel transformer.
//
// The default HTTP client is wired at consumption time (see api.js) — this
// module is dependency-injection-friendly by design so tests can pass a
// fake.

// Timing constants — pinned by tests/test_auth_watchdog_invariant.py so
// nobody can drift them without also updating the AuthHealthMonitor
// watchdog value.
export const AUTH_ME_TIMEOUT_MS = 30000;
export const AUTH_ME_RETRY_BACKOFF_MS = 1500;
export const AUTH_ME_RETRY_TIMEOUT_MS = 15000;

const _isRetryableAuthError = (err) => {
  // Retry on: no response (network error, DNS fail, TCP reset, timeout) OR 5xx.
  // Do NOT retry on: 401, 403 (real auth failure), other 4xx.
  if (!err?.response) return true;
  const status = err.response.status;
  return status >= 500 && status < 600;
};

const _defaultSleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Build a hardened /auth/me caller.
 *
 * @param {(options: {timeout: number}) => Promise<any>} httpAuthMe
 *   Function that performs `GET /auth/me` with the passed timeout and
 *   returns the axios-style response object. Injected so tests never
 *   need to touch real axios.
 * @param {(ms: number) => Promise<void>} sleep
 *   Optional. Used for the retry backoff. Defaults to a real setTimeout
 *   sleeper; tests inject an immediate resolver.
 *
 * @returns async () => Promise<Response>. Throws the original error on
 *   401/403 (fail-fast on real auth failure) or after the single retry
 *   also fails.
 */
export function buildAuthMeCall(httpAuthMe, sleep = _defaultSleep) {
  return async function authMeCall() {
    try {
      return await httpAuthMe({ timeout: AUTH_ME_TIMEOUT_MS });
    } catch (err) {
      const status = err?.response?.status;
      // Real auth failures: fail fast, no retry.
      if (status === 401 || status === 403) throw err;
      // Not a transient error class we know how to recover from.
      if (!_isRetryableAuthError(err)) throw err;
      // One silent retry with backoff, shorter timeout.
      await sleep(AUTH_ME_RETRY_BACKOFF_MS);
      return await httpAuthMe({ timeout: AUTH_ME_RETRY_TIMEOUT_MS });
    }
  };
}
